import { FlightLayerFacade } from '@fe/flight-layer';
import { FeMap } from '@fe/maplibre-extensions';
import { HeatmapStyle, WeatherSettings, WindDepiction } from '../../settings/settings';
import { isSFEWindsProduct as isCaeWindsProduct } from '../../utils/utils';
import { Dispatcher } from '../../worker/dispatcher';
import { CompositeLayer } from '../composite/composite-layer';
import { HeatmapLayer } from '../raster/heatmap-layer';
import { WeatherLayerBase } from '../weather-layer-base';
import { WindBarbsLayer } from './wind-barbs-layer';
import { WindParticlesLayer } from './wind-particles-layer';

export class WindsCompositeLayer extends CompositeLayer {
    private heatmap: WeatherLayerBase | null = null;
    private barbs: WeatherLayerBase | null = null;
    private particles: WeatherLayerBase | null = null;
    private flightLayerFacade: FlightLayerFacade | null = null;

    constructor(
        map: FeMap,
        workspaceId: number,
        dispatcher: Dispatcher,
        productId: number,
        weatherTopSlot: string,
        settings: WeatherSettings,
        flightLayerFacade: FlightLayerFacade | null
    ) {
        super(map, workspaceId, dispatcher, productId, weatherTopSlot, settings);
        this.flightLayerFacade = flightLayerFacade;
    }

    onAdd(): void {
        if (this.settings.winds.windDepiction === WindDepiction.barbs) {
            this.barbs = this.createBarbs();
            this.addLayer(this.barbs);
        } else if (this.settings.winds.windDepiction === WindDepiction.particles && isCaeWindsProduct(this.productId)) {
            this.particles = this.createParticles();
            this.addLayer(this.particles);
        }

        if (this.settings.winds.heatmapStyle !== HeatmapStyle.none && isCaeWindsProduct(this.productId)) {
            this.heatmap = this.createHeatmap();
            this.addLayer(this.heatmap);
        }
    }

    protected onSettingsUpdated(): void {
        if (this.settings.winds.windDepiction === WindDepiction.barbs) {
            if (!this.barbs) {
                // Barbs added
                this.barbs = this.createBarbs();
                this.addLayer(this.barbs);
            }
        } else {
            if (this.barbs) {
                // Barbs removed
                this.removeLayer(this.barbs);
                this.barbs = null;
            }
        }

        // Particles are available only for SFE winds products
        if (this.settings.winds.windDepiction === WindDepiction.particles && isCaeWindsProduct(this.productId)) {
            if (!this.particles) {
                // Particles added
                this.particles = this.createParticles();
                this.addLayer(this.particles);
            }
        } else {
            if (this.particles) {
                // Particles removed
                this.removeLayer(this.particles);
                this.particles = null;
            }
        }

        // Heatmap is available only for SFE winds products
        if (this.settings.winds.heatmapStyle !== HeatmapStyle.none && isCaeWindsProduct(this.productId)) {
            if (!this.heatmap) {
                // Heatmap added
                this.heatmap = this.createHeatmap();
                this.addLayer(this.heatmap);
            }
        } else {
            if (this.heatmap) {
                // Heatmap removed
                this.removeLayer(this.heatmap);
                this.heatmap = null;
            }
        }
    }

    createHeatmap(): WeatherLayerBase {
        return new HeatmapLayer(
            this.map,
            this.workspaceId,
            this.dispatcher,
            this.productId,
            this.barbs?.getBottomSlot() ?? this.slotTopLayerId,
            this.settings,
            'heatmap'
        );
    }

    createBarbs(): WeatherLayerBase {
        return new WindBarbsLayer(this.map, this.workspaceId, this.dispatcher, this.productId, this.slotTopLayerId, this.settings, 'barbs');
    }

    createParticles(): WeatherLayerBase {
        return new WindParticlesLayer(
            this.map,
            this.workspaceId,
            this.dispatcher,
            this.productId,
            this.slotTopLayerId,
            this.settings,
            this.flightLayerFacade,
            'particles'
        );
    }
}
